/**
 * This module contains the [[Observable]] interface and its principal implementation, [[Signal]].
 * See https://tc39.es/proposal-observable/ and https://rxjs.dev/ for more in-depth information
 * about observables.
 */


/**
 * An [[Observer]] is a callback function that will receive `T` values from a [[Observable]]
 * after it has been [[subscribe]]d to it.
 */
export type Observer<T> = (arg: T) => void;


/**
 * A subscription gets returned to you when you [[Observable.subscribe]] an [[Observer]] to an [[Observable]].
 * It is via this object that you can [[unsubscribe]] the observer again.
 */
export interface Subscription {

	/**
	 * Unsubscribes the observer from the [[Observable]] so it will no longer receive its values.
	 */
    unsubscribe(): void;
}


/**
 * An [[Observable]] is a composable sequence of `T` values that will be *pushed* to observers
 * as they become available. This is the reverse of collections, which allow you to *pull* values
 * out of it via iteration.
 *
 *  * `subscription = subscribe(observer)` to start receiving values.
 *
 *  * `subscription.unsubscribe()` to stop receiving values.
 *
 * @example
 *
 * ```typescript
 * import {node} from '.../core/node/Node';
 *
 * const clicks = node(document.querySelector('#someElement')).clicks;
 * const subscription = clicks.subscribe(e => console.log('You clicked on ', e.target));
 * ...
 * subscription.unsubscribe();
 * ```
 *
 * @seealso https://tc39.es/proposal-observable/
 * @seealso https://rxjs.dev/
 */
export class Observable<T> {

    private readonly subscribe_: (observer: Observer<T>) => Subscription;

	/**
	 * Initializes a new instance of the [[Observable]] class.
	 *
	 * @param subscribe The implementation of the [[subscribe]] method.
	 */
    public constructor(subscribe: (observer: Observer<T>) => Subscription) {
        this.subscribe_ = subscribe;
    }

	/**
	 * Returns a new observable where all values of the original observable are delayed
	 * as long as new values are pushed to observers. Once there hasn't been a new value for
	 * `wait` milliseconds, the last value gets pushed.
	 *
	 * @param wait The number of milliseconds to wait after the last value before it is pushed.
	 */
    public debounce(wait: number): Observable<T> {
        return new Observable<T>(observer => {
            let timeout;
            return this.subscribe(arg => {
                const later = () => {
                    timeout = null;
                    observer(arg);
                };
                clearTimeout(timeout);
                timeout = setTimeout(later, wait);
            });
        });
    }

	/**
	 * Gets a new observable containing only the values that fulfill the given predicate `fn`.
	 * Think [Array.prototype.filter()](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/filter).
	 *
	 * @param fn A predicate that specifies which values from the original observable to keep.
	 */
    public filter(fn: (arg: T) => boolean): Observable<T> {
        return new Observable<T>(observer => {
            return this.subscribe(arg => {
                if (fn(arg)) {
                    observer(arg);
                }
            });
        });
    }

	/**
	 * Gets a new observable where each value has been transformed by the given function `fn`.
	 * Think [Array.prototype.map()](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/map).
	 *
	 * @param fn A function that maps a value to another value.
	 */
    public map<U>(fn: (arg: T) => U): Observable<U> {
        return new Observable<U>(observer => {
            return this.subscribe(arg => {
                observer(fn(arg));
            });
        });
    }

	/**
	 * Subscribes the given `observer` (a callback function) to this observable which will start
	 * receiving its values. Returns a [[Subscription]] via which you can [[unsubscribe]] the observer.
	 *
	 * @param observer The callback function that should receive `T` values from this observable.
	 */
    public subscribe(observer: Observer<T>): Subscription {
        return this.subscribe_(observer);
    }
}


/**
 * A kind of [[Observable]] that can dispatch `T` values via [[Signal.dispatch]].
 */
export class Signal<T> extends Observable<T> {

    private observers_?: Observer<T>[];

	/**
	 * Initializes a new instance of the [[Signal]] class.
	 */
    public constructor() {
        super(observer => {
            if (!this.observers_) {
                this.observers_ = [observer];
            }
            else if (this.observers_.indexOf(observer) < 0) {
                this.observers_.push(observer);
            }
            const observers = this.observers_;
            return {
                unsubscribe: () => {
                    const index = observers.indexOf(observer);
                    if (index >= 0) {
                        observers.splice(index, 1);
                    }
                }
            };
        });
        this.observers_ = [];
    }

	/**
	 * Dispatches the given value `arg` to this signal's observers.
	 *
	 * @param arg The value to dispatch to observers.
	 */
    public dispatch(arg: T) {
        if (this.observers_) {
            for (const observer of [...this.observers_]) {
                observer(arg);
            }
        }
    }
}

/**
 * Listens to an event target event and creates an observable instance
 * @param el the event target tzo listen to
 * @param event the name of the event to listen to
 */
export function observable<TEvent extends Event>( event:string, el:EventTarget ):Observable<TEvent> {
	return new Observable( observer => {
		let handler = ( event ) => observer( event );
		el.addEventListener( event, handler );
		return {
			unsubscribe:() => {
				el.removeEventListener( event, handler );
			}
		}
	});
}
